const ENDPOINT_CONFIG_URL = '/config/wx-mini-endpoints';
const NODE_UPLOAD_TEST_PATH = '/health/upload-test';
export const NODE_PROBE_TIMEOUT_MS = 10_000;
export const NODE_PROBE_COUNT = 3;
const NODE_TEST_FILE_NAME = 'node-status-test.bin';
const NODE_TEST_FILE_CONTENT = 'img-cloud-node-status-test';

export const DEFAULT_NODE_ENDPOINTS = [
  'https://imgbed.cn',
  'https://vip.imgbed.cn/',
  'https://2.imgbed.cn/',
] as const;

export type NodeProbeState = 'pending' | 'success' | 'failed';

export interface NodeStatus {
  endpoint: string;
  state: NodeProbeState;
  latencyMs: number | null;
}

const normalizeEndpoints = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value.reduce<string[]>((result, item) => {
    if (typeof item !== 'string') return result;
    const endpoint = item.trim();
    if (endpoint && !seen.has(endpoint)) {
      seen.add(endpoint);
      result.push(endpoint);
    }
    return result;
  }, []);
};

export const loadNodeEndpoints = async (): Promise<string[]> => {
  try {
    const response = await fetch(ENDPOINT_CONFIG_URL, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await response.json() as { success?: boolean; data?: { endpoints?: unknown } };
    if (payload.success === false) throw new Error('Endpoint configuration disabled');
    const endpoints = normalizeEndpoints(payload.data?.endpoints);
    return endpoints.length > 0 ? endpoints : [...DEFAULT_NODE_ENDPOINTS];
  } catch (error) {
    console.warn('Failed to load mini-program endpoints; using defaults', error);
    return [...DEFAULT_NODE_ENDPOINTS];
  }
};

export const probeNodeEndpoint = async (endpoint: string): Promise<NodeStatus> => {
  const uploadTestUrl = `${endpoint.replace(/\/+$/, '')}${NODE_UPLOAD_TEST_PATH}`;
  let totalLatencyMs = 0;

  for (let attempt = 0; attempt < NODE_PROBE_COUNT; attempt += 1) {
    const result = await probeUploadOnce(uploadTestUrl);
    if (!result.success) {
      return { endpoint, state: 'failed', latencyMs: result.latencyMs };
    }
    totalLatencyMs += result.latencyMs;
  }

  return {
    endpoint,
    state: 'success',
    latencyMs: Math.round(totalLatencyMs / NODE_PROBE_COUNT),
  };
};

interface ProbeAttemptResult {
  success: boolean;
  latencyMs: number;
}

const createProbeFormData = (): FormData => {
  const formData = new FormData();
  const file = new Blob([NODE_TEST_FILE_CONTENT], { type: 'application/octet-stream' });
  formData.append('file', file, NODE_TEST_FILE_NAME);
  return formData;
};

const probeUploadOnce = async (url: string): Promise<ProbeAttemptResult> => {
  const startedAt = performance.now();
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), NODE_PROBE_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      method: 'POST',
      body: createProbeFormData(),
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });
    const body = await response.text();
    return {
      success: response.ok && body.trim() === 'success',
      latencyMs: Math.round(performance.now() - startedAt),
    };
  } catch (error) {
    return {
      success: false,
      latencyMs: Math.round(performance.now() - startedAt),
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
};
